import { useEffect, useRef, useState } from "react";
import { MoreVertical, UserCog, Pencil, UserX, UserCheck, Trash2, Crown, X, Save } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

import { ROLE_LABELS as ALL_LABELS, ROLE_TECH } from "../lib/roles";

const ROLE_LABELS = { owner: ALL_LABELS.owner, admin: ALL_LABELS.admin, [ROLE_TECH]: ALL_LABELS[ROLE_TECH] };
const inputCls = "w-full px-3 py-2 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 rounded-sm";

export function MemberMenu({ member, me, onChanged }) {
    const [open, setOpen] = useState(false);
    const [modal, setModal] = useState(null); // role | edit | transfer | remove
    const [form, setForm] = useState({});
    const ref = useRef(null);
    const isOwner = me?.role === "owner";
    const isSelf = member.id === me?.id;

    useEffect(() => {
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    // Administrátor smí pracovat jen s vedoucími techniky (pozvat / deaktivovat); vše ostatní je jen pro vlastníka
    if (!isOwner && member.role !== ROLE_TECH) return null;

    const run = async (fn, okMsg) => {
        try { await fn(); toast.success(okMsg); setModal(null); setOpen(false); onChanged(); }
        catch (e) { toast.error(e.response?.data?.detail || "Akce selhala"); }
    };
    const openModal = (kind) => {
        setOpen(false);
        setForm(kind === "role" ? { role: member.role } : { name: member.name || "", phone: member.phone || "" });
        setModal(kind);
    };

    const items = [
        isOwner && !isSelf && { k: "role", icon: UserCog, label: "Změnit roli", testid: "action-role" },
        isOwner && { k: "edit", icon: Pencil, label: "Upravit jméno a telefon", testid: "action-edit" },
        !isSelf && (member.is_active
            ? { k: "deactivate", icon: UserX, label: "Deaktivovat člena", testid: "action-deactivate", run: () => run(() => api.post(`/team/members/${member.id}/deactivate`), "Člen deaktivován — nemůže se přihlásit, jeho záznamy zůstávají") }
            : { k: "activate", icon: UserCheck, label: "Znovu aktivovat", testid: "action-activate", run: () => run(() => api.post(`/team/members/${member.id}/activate`), "Člen znovu aktivován") }),
        isOwner && !isSelf && member.is_active && member.role !== "owner" && { k: "transfer", icon: Crown, label: "Předat vlastnictví", testid: "action-transfer" },
        isOwner && !isSelf && { k: "remove", icon: Trash2, label: "Odebrat z firmy", testid: "action-remove", danger: true },
    ].filter(Boolean);

    return (
        <div className="relative inline-block text-left" ref={ref}>
            <button onClick={() => setOpen(!open)} data-testid={`member-menu-${member.id}`} className="p-1.5 min-w-[44px] min-h-[44px] inline-flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-sm">
                <MoreVertical className="w-4 h-4" />
            </button>
            {open && (
                <div className="absolute right-0 mt-1 w-60 bg-white border-2 border-slate-900 shadow-lg z-30 py-1" data-testid={`member-menu-items-${member.id}`}>
                    {items.map((it) => (
                        <button key={it.k} data-testid={`${it.testid}-${member.id}`} onClick={() => (it.run ? it.run() : openModal(it.k))}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-orange-50 ${it.danger ? "text-red-700" : "text-slate-800"}`}>
                            <it.icon className="w-4 h-4" /> {it.label}
                        </button>
                    ))}
                </div>
            )}

            {modal && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setModal(null)}>
                    <div className="bg-white border-2 border-slate-900 max-w-md w-full text-left" onClick={(e) => e.stopPropagation()} data-testid={`member-modal-${modal}`}>
                        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-slate-900">
                            <h2 className="font-display text-lg font-bold">
                                {modal === "role" ? "Změnit roli" : modal === "edit" ? "Upravit člena" : modal === "transfer" ? "Předat vlastnictví" : "Odebrat z firmy"}
                            </h2>
                            <button onClick={() => setModal(null)}><X className="w-5 h-5 text-slate-400 hover:text-slate-900" /></button>
                        </div>
                        <div className="p-6 space-y-4 text-sm">
                            <div className="text-slate-600"><b className="text-slate-900">{member.name || member.email}</b> · {member.email}</div>
                            {modal === "role" && (
                                <select value={form.role} onChange={(e) => setForm({ role: e.target.value })} data-testid="role-modal-select" className={inputCls}>
                                    {Object.entries(ROLE_LABELS).filter(([k]) => isOwner || k !== "owner").map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                </select>
                            )}
                            {modal === "edit" && (
                                <>
                                    <div><label className="label-eyebrow block mb-1.5">Jméno *</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-member-name" className={inputCls} /></div>
                                    <div><label className="label-eyebrow block mb-1.5">Telefon</label><input type="tel" inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="edit-member-phone" className={inputCls} placeholder="+420 …" /></div>
                                </>
                            )}
                            {modal === "transfer" && (
                                <div className="bg-amber-50 border border-amber-300 p-3 text-amber-900">
                                    Člen <b>{member.name || member.email}</b> se stane <b>Vlastníkem</b> firmy. Vy zůstanete ve firmě jako <b>Administrátor</b>. Tuto akci může vrátit jen nový vlastník.
                                </div>
                            )}
                            {modal === "remove" && (
                                <div className="bg-red-50 border border-red-300 p-3 text-red-900">
                                    Člen ztratí přístup do firmy. Jeho jméno u záznamů v deníku, zakázkách a fakturách zůstane zachováno. Pokud chcete přístup jen pozastavit, použijte raději <b>Deaktivovat</b>.
                                </div>
                            )}
                        </div>
                        <div className="flex gap-2 px-6 py-4 bg-slate-50 border-t border-slate-200 justify-end">
                            <button onClick={() => setModal(null)} className="px-4 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-100 rounded-sm">Zrušit</button>
                            <button data-testid="member-modal-confirm"
                                    onClick={() => {
                                        if (modal === "role") run(() => api.put(`/team/members/${member.id}/role`, { role: form.role }), "Role změněna");
                                        if (modal === "edit") run(() => api.put(`/team/members/${member.id}`, form), "Údaje uloženy");
                                        if (modal === "transfer") run(() => api.post(`/team/members/${member.id}/transfer-ownership`), "Vlastnictví předáno");
                                        if (modal === "remove") run(() => api.delete(`/team/members/${member.id}`), "Člen odebrán z firmy");
                                    }}
                                    className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold text-white rounded-sm inline-flex items-center gap-1 ${modal === "remove" ? "bg-red-600 hover:bg-red-700" : "bg-orange-600 hover:bg-orange-700"}`}>
                                <Save className="w-3.5 h-3.5" /> {modal === "remove" ? "Odebrat" : modal === "transfer" ? "Předat vlastnictví" : "Uložit"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
